import { useState, useEffect, useCallback } from 'react';
import { CreditCard, Loader2, TrendingUp, Clock, CheckCircle2, XCircle, AlertCircle } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const statusConfig = {
  paid: { label: 'Payé', color: 'bg-emerald-100 text-emerald-700', icon: CheckCircle2 },
  pending: { label: 'En attente', color: 'bg-amber-100 text-amber-700', icon: Clock },
  initiated: { label: 'Initié', color: 'bg-blue-100 text-blue-700', icon: Clock },
  failed: { label: 'Échoué', color: 'bg-red-100 text-red-700', icon: XCircle },
};

const getStatus = (tx) => {
  const ps = tx.payment_status || tx.status || 'pending';
  return statusConfig[ps] || statusConfig.pending;
};

const formatXPF = (amount) => {
  if (!amount) return '0 F';
  return Number(amount).toLocaleString('fr-FR') + ' F';
};

const AdminPaymentsTab = ({ token }) => {
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');

  const headers = { Authorization: `Bearer ${token}` };

  const fetchPayments = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/payments`, { headers });
      setTransactions(res.data || []);
    } catch {
      toast.error('Erreur chargement des paiements');
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    fetchPayments();
  }, [fetchPayments]);

  const filtered = filter === 'all'
    ? transactions
    : transactions.filter(tx => (tx.payment_status || tx.status) === filter);

  const paidTx = transactions.filter(tx => tx.payment_status === 'paid');
  const totalRevenue = paidTx.reduce((sum, tx) => sum + (tx.amount_xpf || 0), 0);
  const pendingCount = transactions.filter(tx => tx.payment_status === 'pending' || tx.status === 'initiated').length;

  if (loading) {
    return (
      <div className="flex justify-center py-20">
        <Loader2 className="w-8 h-8 animate-spin text-teal-600" />
      </div>
    );
  }

  return (
    <div data-testid="payments-tab">
      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
        <div className="bg-white rounded-xl border border-slate-200 p-5" data-testid="stat-total-tx">
          <div className="flex items-center gap-2 text-slate-500 text-xs font-medium mb-2">
            <CreditCard className="w-3.5 h-3.5" /> TRANSACTIONS
          </div>
          <p className="text-2xl font-bold text-slate-900">{transactions.length}</p>
        </div>
        <div className="bg-white rounded-xl border border-slate-200 p-5" data-testid="stat-revenue">
          <div className="flex items-center gap-2 text-emerald-600 text-xs font-medium mb-2">
            <TrendingUp className="w-3.5 h-3.5" /> REVENUS CONFIRMÉS
          </div>
          <p className="text-2xl font-bold text-emerald-700">{formatXPF(totalRevenue)}</p>
          <p className="text-xs text-slate-400 mt-1">{paidTx.length} paiement{paidTx.length !== 1 ? 's' : ''}</p>
        </div>
        <div className="bg-white rounded-xl border border-slate-200 p-5" data-testid="stat-pending">
          <div className="flex items-center gap-2 text-amber-600 text-xs font-medium mb-2">
            <Clock className="w-3.5 h-3.5" /> EN ATTENTE
          </div>
          <p className="text-2xl font-bold text-amber-700">{pendingCount}</p>
        </div>
        <div className="bg-white rounded-xl border border-slate-200 p-5" data-testid="stat-success-rate">
          <div className="flex items-center gap-2 text-teal-600 text-xs font-medium mb-2">
            <CheckCircle2 className="w-3.5 h-3.5" /> TAUX DE CONVERSION
          </div>
          <p className="text-2xl font-bold text-teal-700">
            {transactions.length > 0 ? Math.round((paidTx.length / transactions.length) * 100) : 0}%
          </p>
        </div>
      </div>

      {/* Filters */}
      <div className="flex items-center gap-3 mb-4">
        <div className="flex gap-1 bg-white border border-slate-200 rounded-lg p-1">
          {[
            { id: 'all', label: 'Tous' },
            { id: 'paid', label: 'Payés' },
            { id: 'pending', label: 'En attente' },
            { id: 'initiated', label: 'Initiés' },
          ].map(f => (
            <button
              key={f.id}
              onClick={() => setFilter(f.id)}
              className={`px-3 py-1.5 text-xs font-medium rounded-md transition-all ${filter === f.id ? 'bg-teal-600 text-white' : 'text-slate-600 hover:bg-slate-50'}`}
              data-testid={`filter-payments-${f.id}`}
            >
              {f.label}
            </button>
          ))}
        </div>
        <span className="text-sm text-slate-500">{filtered.length} résultat{filtered.length !== 1 ? 's' : ''}</span>
      </div>

      {/* Table */}
      {filtered.length === 0 ? (
        <div className="text-center py-16 bg-white rounded-xl border border-slate-200">
          <CreditCard className="w-12 h-12 text-slate-300 mx-auto mb-3" />
          <p className="text-slate-500">Aucune transaction</p>
        </div>
      ) : (
        <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 border-b border-slate-200">
                <tr>
                  <th className="text-left p-3 font-semibold text-slate-600">Date</th>
                  <th className="text-left p-3 font-semibold text-slate-600">Client</th>
                  <th className="text-left p-3 font-semibold text-slate-600">Article</th>
                  <th className="text-right p-3 font-semibold text-slate-600">Montant</th>
                  <th className="text-center p-3 font-semibold text-slate-600">Statut</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((tx, i) => {
                  const st = getStatus(tx);
                  const Icon = st.icon;
                  return (
                    <tr key={tx.id || i} className="border-b border-slate-100 last:border-0 hover:bg-slate-50/50" data-testid={`payment-row-${i}`}>
                      <td className="p-3 text-slate-500 whitespace-nowrap">
                        {tx.created_at ? new Date(tx.created_at).toLocaleDateString('fr-FR', { day: '2-digit', month: 'short', year: 'numeric' }) : '-'}
                      </td>
                      <td className="p-3">
                        <p className="font-medium text-slate-900">{tx.customer_name || '-'}</p>
                        <p className="text-xs text-slate-400">{tx.customer_email || ''}</p>
                      </td>
                      <td className="p-3">
                        <p className="text-slate-800">{tx.package_title || tx.package_id || '-'}</p>
                        <p className="text-xs text-slate-400">{tx.package_id}</p>
                      </td>
                      <td className="p-3 text-right font-semibold text-slate-900 whitespace-nowrap">
                        {formatXPF(tx.amount_xpf)}
                      </td>
                      <td className="p-3 text-center">
                        <span className={`inline-flex items-center gap-1 text-xs px-2 py-1 rounded-full font-medium ${st.color}`}>
                          <Icon className="w-3 h-3" />
                          {st.label}
                        </span>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminPaymentsTab;
